import { useState } from "react";

// A single-series column chart in plain HTML/CSS. Single series, so no legend —
// the section title names what's plotted. Specs: columns capped at 24px with a
// 4px rounded data-end and a square baseline, hairline recessive gridlines,
// compact y-ticks, one direct label (the latest value), a per-column tooltip
// with a hit target the full width of its slot, and a table view.

const MARK = "#2F8A57"; // brand-family green, validated: lightness, chroma, contrast

function niceMax(v) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

export default function ColumnChart({ data, format, compact, height = 170, title }) {
  const [hover, setHover] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const max = niceMax(Math.max(0, ...data.map((d) => d.value)));
  const ticks = [0, max / 2, max];
  const last = data.length - 1;

  return (
    <div>
      <div className="flex justify-end mb-1">
        <button
          onClick={() => setAsTable((v) => !v)}
          className="text-[11px] font-semibold text-birdie-charcoal/50 hover:text-birdie-green"
        >
          {asTable ? "Show chart" : "Show as table"}
        </button>
      </div>

      {asTable ? (
        <table className="w-full text-sm">
          <caption className="sr-only">{title}</caption>
          <tbody>
            {data.map((d) => (
              <tr key={d.key} className="border-b border-birdie-green/10 last:border-0">
                <td className="py-1.5 text-birdie-charcoal/70">{d.long || d.label}</td>
                <td className="py-1.5 text-right tabular-nums text-birdie-charcoal">{format(d.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="flex gap-2" role="group" aria-label={title}>
          {/* y-axis */}
          <div className="relative w-9 shrink-0 text-[10px] text-birdie-charcoal/45" style={{ height }}>
            {ticks.map((t) => (
              <div
                key={t}
                className="absolute right-0 -translate-y-1/2 tabular-nums"
                style={{ top: `${(1 - t / max) * 100}%` }}
              >
                {compact(t)}
              </div>
            ))}
          </div>

          <div className="flex-1 min-w-0">
            <div className="relative" style={{ height }}>
              {/* hairline gridlines, recessive */}
              {ticks.map((t) => (
                <div
                  key={t}
                  className="absolute inset-x-0 border-t border-birdie-charcoal/10"
                  style={{ top: `${(1 - t / max) * 100}%` }}
                />
              ))}

              <div className="absolute inset-0 flex items-end">
                {data.map((d, i) => {
                  const h = (d.value / max) * 100;
                  const showLabel = i === last && d.value > 0;
                  return (
                    <button
                      key={d.key}
                      type="button"
                      aria-label={`${d.long || d.label}: ${format(d.value)}`}
                      onMouseEnter={() => setHover(i)}
                      onMouseLeave={() => setHover(null)}
                      onFocus={() => setHover(i)}
                      onBlur={() => setHover(null)}
                      onClick={() => setHover(i)}
                      className="relative flex-1 min-w-0 h-full flex items-end justify-center focus:outline-none"
                    >
                      {/* the mark: <=24px, 4px rounded data-end, square baseline */}
                      <span
                        className="block w-[60%] max-w-[24px] rounded-t transition-opacity"
                        style={{
                          height: `${h}%`,
                          minHeight: d.value > 0 ? 2 : 0,
                          background: MARK,
                          opacity: hover === null || hover === i ? 1 : 0.45,
                        }}
                      />
                      {showLabel && hover === null && (
                        <span
                          className="absolute left-1/2 -translate-x-1/2 text-[10px] font-semibold text-birdie-charcoal whitespace-nowrap"
                          style={{ bottom: `calc(${h}% + 4px)` }}
                        >
                          {compact(d.value)}
                        </span>
                      )}
                      {hover === i && (
                        <span
                          className={`absolute z-10 ${
                            // Keep edge tooltips on-screen: anchor them inward.
                            i < 2 ? "left-0" : i > last - 2 ? "right-0" : "left-1/2 -translate-x-1/2"
                          } rounded-lg bg-birdie-charcoal text-white text-[11px] px-2 py-1 whitespace-nowrap shadow-lg pointer-events-none`}
                          style={{ bottom: `calc(${h}% + 6px)` }}
                        >
                          <span className="opacity-70">{d.long || d.label}</span>{" "}
                          <span className="font-semibold">{format(d.value)}</span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* x-axis labels */}
            <div className="flex mt-1">
              {data.map((d) => (
                <div key={d.key} className="flex-1 min-w-0 text-center text-[10px] text-birdie-charcoal/45 overflow-hidden whitespace-nowrap">
                  {d.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
